import { withDialog } from "../compat/jquery-ui-dialog";
/**
 * HandleAddToCartEvent — the ATC branch tree (REQ-CHK-3; research/02 §9;
 * research/05 §2.1), verbatim from v1 process_product_page.js: re-entrancy
 * guard; loader show; GetVariantProperties() BEFORE ValidateInputs()
 * (research/02 §12.11); flag set; POST /variant with productId read live from
 * window.meta.product.id; dead quantity-from-t.value block preserved as
 * no-op; Branch A (object → showDialog()), Branch B (cpc_ token: sentinel
 * guard, token store, #variantIdCustom = baseVariantId, flags flip), Branch C
 * (variant id); common tail UpdateAllSelectedCheckBox →
 * UpdateAllHiddenElements (destructive removal — research/02 §12.12) →
 * routing (drawer path requires window.cpcThemeCartType === 'drawer' AND
 * cpc_cartDrawerEnabled); .fail message mapping with quote-stripping.
 * cpc_isCheckoutInProgress is NEVER reset on success (research/02 §12.1;
 * BUGS.md preserve).
 *
 * Branch B (DIV-026): the variant written alongside the `cpc_` price token
 * is resolveVariantId()'s size-matched pick when the calculator answers
 * every one of the product's options, else appState.baseVariantId exactly
 * as before — see checkout/variant-resolution.ts.
 *
 * Also owns the calculator-element discovery used at ATC time: the stored
 * reference is set during render placement (bootstrap/product-page.ts calls
 * setCurrentCalculatorElement); the Fallback branches below are dead code in
 * practice (ATC-015, checkout SKIPPED.md) but preserved verbatim.
 *
 * all jQuery is the BUNDLED copy (DIV-004).
 */
import $ from "jquery";
import { cpcAjax } from "../compat/jquery-ajax";
import { appState } from "../state/app-state";
import { showDialog } from "../common/common-utils";
import { t as translate } from "../i18n/translate";
import { GetVariantProperties } from "./variant-properties";
import { resolveVariantId } from "./variant-resolution";
import { SubmitFormData } from "./submit-form-data";
import { SubmitViaThemeNativeSubmit } from "./submit-native";
import {
  ValidateInputs,
  UpdateAllHiddenElements,
  RestoreAllHiddenElements,
  UpdateAllSelectedCheckBox,
} from "./validate-inputs";

// Global variable to store calculator element reference for app blocks
let currentCalculatorElement: any = null;

/** Render placement stores the element the ATC handler will read. */
export function setCurrentCalculatorElement(element: any): void {
  currentCalculatorElement = element;
}

// Helper function to get current calculator element with fallback logic
function getCurrentCalculatorElement(): any {
  // Return stored reference if available
  if (currentCalculatorElement) {
    return currentCalculatorElement;
  }

  // Fallback: Search for calculator elements
  const appBlock = document.querySelector(".cpc-calculator-target");
  if (appBlock) {
    console.log("Fallback: Found app block calculator");
    return appBlock;
  }

  const legacyBlock = document.querySelector("#calculator-block");
  if (legacyBlock) {
    console.log("Fallback: Found legacy calculator block");
    return legacyBlock;
  }

  const calculatorDiv = document.querySelector("#calculator");
  if (calculatorDiv) {
    console.log("Fallback: Found calculator div");
    return calculatorDiv.parentElement;
  }

  console.log("No calculator element found");
  return null;
}

export async function HandleAddToCartEvent(t: any): Promise<void> {
  t.preventDefault();
  t.stopImmediatePropagation();
  t.stopPropagation();

  // Guard: prevent duplicate submissions while /variant API is in-flight
  if (appState.cpc_isCheckoutInProgress) return;

  $(".loading-anim-cpc").show();

  var props = GetVariantProperties();

  var validationError = ValidateInputs();
  if (validationError === true) {
    $(".loading-anim-cpc").hide();
    return;
  }

  // Re-read the live variant selection at ATC time. baseVariantId is stamped
  // once from variants[0] at page load; themes update the (renamed) oldId
  // input/select when the customer picks a variant — often programmatically,
  // with no change event (e.g. Teapoy's variant-radios on a disabled input).
  // Reading .value works regardless of the disabled attribute.
  if (appState.productForm) {
    const liveVariantEl = (appState.productForm as any).querySelector(
      'input[name="oldId"], select[name="oldId"]',
    ) as HTMLInputElement | HTMLSelectElement | null;
    if (liveVariantEl && liveVariantEl.value && liveVariantEl.value !== "0") {
      appState.baseVariantId = liveVariantEl.value as any;
      console.log("CPC: baseVariantId refreshed from form at ATC time:", appState.baseVariantId);
    }
  }

  appState.cpc_isCheckoutInProgress = true;

  //Create Variant
  // DIV-043: cpcAjax, not $.ajax — immune to a theme app patching the shared
  // jQuery's .ajax (see compat/jquery-ajax.ts).
  cpcAjax({
    // DIV-002: `type:` not `method:` — identical on jQuery >=1.9, but pre-1.9
    // theme copies ignore `method:` and degrade to GET -> 405 (issue #35).
    type: "POST",
    url: appState.baseCalculatorNewApiUrl + "variant",
    data: JSON.stringify({
      calculatorId: appState.cpc_calculatorId,
      productId: window.meta.product.id,
      properties: { ...props },
    }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .done(function (response: any) {
      var quantity = 1;
      var quantSelector = $("#Quantity");
      if (quantSelector) {
        var n = t.value;
        if (n) {
          quantity = Number(n);
        }
      }

      if (typeof response === "object" && response !== null) {
        appState.cpc_isCheckoutInProgress = false;
        showDialog();
        $(".loading-anim-cpc").hide();
        return;
      }
      else if (typeof response === "string" && response.startsWith("cpc_")) {
        console.log("Received CPC Price Token:", response);
        var cpcPriceToken = document.getElementById("cpcPriceToken") as any;

        // Guard: never submit with a "none" placeholder token.
        // If the API returned a valid cpc_ token, store it; otherwise abort and
        // surface an error so the customer can try again. Submitting with "none"
        // causes the Cart Transform function to find no matching token at checkout
        // and fall back to the base variant price — the core bug this fix addresses.
        if (!response || response === "cpc_none" || response.trim() === "cpc_") {
          console.error("CPC: received invalid price token from API, aborting ATC to prevent wrong price at checkout:", response);
          appState.cpc_isCheckoutInProgress = false;
          $(".loading-anim-cpc").hide();
          $("#errDialog").attr("title", "Error");
          $("#dialogMsg").text(translate("Something went wrong calculating your price. Please try again."));
          withDialog(() => ($("#errDialog") as any).dialog({ modal: true }));
          return;
        }

        cpcPriceToken.value = response;

        // DIV-026: in CT Non-Variant mode there is usually no native Shopify
        // variant picker for the shopper to change, so appState.baseVariantId
        // never moves off variants[0] — resolve the variant that matches the
        // calculator's own answers to the product's options (e.g. "Size")
        // first, and fall back to the base variant exactly as before when no
        // confident match exists.
        var variantInput = document.getElementById("variantIdCustom") as any;
        var resolvedVariantId = resolveVariantId(
          props,
          appState.cpc_productOptionNames,
          appState.cpc_productVariants,
        );
        variantInput.value = resolvedVariantId !== null ? resolvedVariantId : appState.baseVariantId;

        //No need to wait for cart confirmation for new checkout flow
        appState.cpc_waitforcartconfirmation = false;
        appState.cpc_isNewCheckoutFlowEnabled = true;
      }
      else {
        console.log("Received CPC VariantId", response);
        var variantInput = document.getElementById("variantIdCustom") as any;
        variantInput.value = response;
      }

      UpdateAllSelectedCheckBox();
      UpdateAllHiddenElements();

      // Pass calculator element for app block support
      const calculatorElement = getCurrentCalculatorElement();
      console.log("Calling add to cart with calculator element:", calculatorElement);

      console.log("CPC: cpc_isNewCheckoutFlowEnabled =", appState.cpc_isNewCheckoutFlowEnabled);

      if (appState.cpc_isNewCheckoutFlowEnabled === true) {
        // New checkout flow (Shopify Functions)
        var cartType = window.cpcThemeCartType;
        console.log("CPC: theme cart type =", cartType);
        console.log("CPC: cart drawer integration enabled =", appState.cpc_cartDrawerEnabled);

        if (cartType === "drawer" && appState.cpc_cartDrawerEnabled === true) {
          // Theme has a cart drawer AND store has cart drawer integration enabled
          console.log("CPC: using SubmitViaThemeNativeSubmit");
          SubmitViaThemeNativeSubmit(calculatorElement);
          // DIV-006 (BUG-020): the drawer flow never navigates, so without a
          // reset every later ATC click was swallowed by the in-flight guard
          // and UpdatePrice() froze — one add per page load. Reset HERE, after
          // the call returns, so the submitter's internal token-guard aborts
          // are covered too. The redirect branches below stay unreset (they
          // navigate; the guard doubles as double-click protection there).
          appState.cpc_isCheckoutInProgress = false;
          // DIV-006 (BUG-028): re-enable hidden-element inputs after the
          // theme has read the form (mirrors the 500ms injected-input
          // cleanup window in SubmitViaThemeNativeSubmit).
          setTimeout(RestoreAllHiddenElements, 600);
        } else {
          // Cart type is not drawer, or cart drawer integration is disabled for this store
          console.log("CPC: using SubmitFormData → redirect to /cart");
          SubmitFormData(calculatorElement);
        }
      } else {
        // Old checkout flow: our own AJAX POST + redirect to /cart
        console.log("CPC: old checkout flow, using SubmitFormData → redirect to /cart");
        SubmitFormData(calculatorElement);
      }

      return;
    })
    .fail(function (jqXHR: any, textStatus: any, errorThrown: any) {
      // Reset so the user can retry after a failed /variant call
      appState.cpc_isCheckoutInProgress = false;
      $(".loading-anim-cpc").hide();

      console.error("Error:", textStatus, errorThrown);
      let errorMessage = "An error occurred during the request: " + textStatus;
      if (jqXHR.status === 400) {
        errorMessage = jqXHR.responseText;
      } else if (jqXHR.status === 500) {
        errorMessage = jqXHR.responseText;
      } else if (textStatus === "timeout") {
        errorMessage = "Request timed out.";
      } else if (textStatus === "abort") {
        errorMessage = "Request was aborted.";
      }
      $("#errDialog").attr("title", "Error");
      $("#dialogMsg").text(errorMessage.replace(/"/g, ""));
      withDialog(() => ($("#errDialog") as any).dialog({ modal: true }));
    });
}
